import { useMutationState } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import {
	type Dispatch,
	type FC,
	type SetStateAction,
	useMemo,
	useState
} from 'react'
import { createPortal } from 'react-dom'
import { useHotkeys } from 'react-hotkeys-hook'
import { useOutletContext } from 'react-router'
import { ImageCard } from '@/components/commons/image-card'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	MinusCircleIcon,
	XCloseIcon
} from '@/components/ui/icons'
import { ListHeader } from '@/components/ui/list-header'
import { Overlay } from '@/components/ui/overlay'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { AllocateDropdown } from '@/features/photoset/components/allocate-dropdown'
import { ImagesList } from '@/features/photoset/components/images-list'
import type {
	HttpAllocateImagesOptions,
	HttpDeallocateImagesOptions
} from '@/features/photoset/http/photoset.http'
import { useDeallocateImages } from '@/features/photoset/queries/use-deallocate-images'
import { useReallocateImages } from '@/features/photoset/queries/use-reallocate-images'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type {
	OnAllocateAction,
	PhotosetSpace,
	PhotosetURLParams
} from '@/features/photoset/types'
import {
	handleImagesRangeSelect,
	spaceNameToLabel
} from '@/features/photoset/utils'
import { errorHandler } from '@/helpers/utils'
import { MUTATION_KEYS } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

const SpaceDetailEndSlot = ({
	selectedImagesCount,
	currentSpaceImagesCount,
	unselectAll,
	setLastSelectedImageId,
	selectImages,
	currentSpaceImageIds,
	firstImageId,
	allocateDropdown,
	deallocateButton
}: {
	selectedImagesCount: number
	currentSpaceImagesCount: number
	unselectAll: () => void
	setLastSelectedImageId: (id: number | null) => void
	selectImages: (ids: number[]) => void
	currentSpaceImageIds: number[]
	firstImageId: number | undefined
	allocateDropdown: ReactNode
	deallocateButton: ReactNode
}) => {
	const { t } = useTranslation()
	if (selectedImagesCount >= 1) {
		return (
			<>
				<div className='flex items-center gap-2'>
					<h4 className='text-neutral-700 text-sm'>
						{t('common:x_images_selected', {
							count: selectedImagesCount
						})}
					</h4>
					<ButtonLegacy
						onClick={() => {
							unselectAll()
							setLastSelectedImageId(null)
						}}
						size='sm'
						type='button'
						variant='tertiary'
					>
						{t('common:unselect_all')}
					</ButtonLegacy>
				</div>

				{allocateDropdown}

				{deallocateButton}
			</>
		)
	}
	if (currentSpaceImagesCount >= 1) {
		return (
			<ButtonLegacy
				onClick={() => {
					selectImages(currentSpaceImageIds)
					if (firstImageId !== undefined) {
						setLastSelectedImageId(firstImageId)
					}
				}}
				size='sm'
				type='button'
				variant='tertiary'
			>
				{t('common:select_all')}
			</ButtonLegacy>
		)
	}
	return false
}

interface SpaceDetailModalProps {
	onClose: () => void
	currentSpaceIndex: number
	setCurrentSpaceIndex: Dispatch<SetStateAction<number | null>>
	photosetSpaces: PhotosetSpace[]
}

export const SpaceDetailModal: FC<SpaceDetailModalProps> = ({
	onClose,
	currentSpaceIndex,
	setCurrentSpaceIndex,
	photosetSpaces
}) => {
	const { t } = useTranslation()
	const [modalRef, setModalRef] = useState<HTMLDivElement | null>(null)
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const images = usePhotosetStore((state) => state.images)
	const selectedImages = usePhotosetStore((state) => state.selectedImages)
	const unselectAll = usePhotosetStore((state) => state.unselectAll)
	const selectImages = usePhotosetStore((state) => state.selectImages)
	const unselectImages = usePhotosetStore((state) => state.unselectImages)
	const allocateImages = usePhotosetStore((state) => state.allocateImages)
	const deallocateImages = usePhotosetStore((state) => state.deallocateImages)
	const prevIndex =
		currentSpaceIndex === 0
			? photosetSpaces.length - 1
			: currentSpaceIndex - 1
	const nextIndex =
		currentSpaceIndex === photosetSpaces.length - 1
			? 0
			: currentSpaceIndex + 1
	const currentSpaceImages = [...images.values()].filter(
		(image) =>
			image.space?.key_name ===
			photosetSpaces[currentSpaceIndex].space.key_name
	)
	const [lastSelectedImageId, setLastSelectedImageId] = useState<
		number | null
	>(null)
	const reallocateImagesMutation = useReallocateImages()
	const deallocateImagesMutation = useDeallocateImages({
		onError: (error) => errorHandler(error)
	})

	const onDeallocate = async ({
		photosetSpaceId,
		imagesIds,
		keepSelected = false,
		trackInHistory = false
	}: OnAllocateAction) => {
		try {
			await deallocateImagesMutation.mutateAsync({
				urlParams: {
					photosetId: urlParams.id,
					photosetSpaceId
				},
				payload: imagesIds
			})

			deallocateImages({
				imagesIds,
				photosetSpaceId,
				keepSelected,
				trackInHistory
			})
		} catch (error) {
			errorHandler(error)
		}
	}

	const onAllocate = async ({
		photosetSpaceId,
		imagesIds = selectedImages
	}: OnAllocateAction) => {
		await onDeallocate({
			photosetSpaceId,
			imagesIds,
			keepSelected: false,
			trackInHistory: false
		})

		try {
			await reallocateImagesMutation.mutateAsync({
				urlParams: {
					photosetId: urlParams.id,
					photosetSpaceId
				},
				payload: imagesIds
			})

			allocateImages({
				imagesIds,
				photosetSpaceId,
				keepSelected: false,
				trackInHistory: false
			})

			unselectAll()
		} catch (error) {
			errorHandler(error)
		}
	}

	const goToPrevSpace = () => {
		setCurrentSpaceIndex(prevIndex)
		unselectAll()
	}
	const goToNextSpace = () => {
		setCurrentSpaceIndex(nextIndex)
		unselectAll()
	}

	const allocateMutationState = useMutationState({
		filters: {
			mutationKey: [MUTATION_KEYS.ALLOCATE_IMAGES],
			status: 'pending'
		},
		select: (mutation) => ({
			variables: mutation.state.variables as HttpAllocateImagesOptions
		})
	})

	const deallocateMutationState = useMutationState({
		filters: {
			mutationKey: [MUTATION_KEYS.DEALLOCATE_IMAGES],
			status: 'pending'
		},
		select: (mutation) => ({
			variables: mutation.state.variables as HttpDeallocateImagesOptions
		})
	})

	const pendingAllocationImageIds = useMemo(
		() =>
			[...allocateMutationState, ...deallocateMutationState].map(
				(allocateState) => ({
					imageIds: allocateState.variables.payload
				})
			),
		[allocateMutationState, deallocateMutationState]
	)

	useHotkeys('ArrowLeft', goToPrevSpace, {
		preventDefault: true,
		enableOnFormTags: true
	})
	useHotkeys('ArrowRight', goToNextSpace, {
		preventDefault: true,
		enableOnFormTags: true
	})
	useHotkeys('Esc', onClose, { preventDefault: true, enableOnFormTags: true })

	return createPortal(
		<>
			<Overlay className='backdrop-blur-[16px]' />
			<div
				className='fixed inset-0 z-(--z-modal) flex h-full w-full items-center px-6 py-8'
				ref={setModalRef}
			>
				<button
					className='flex w-[180px] shrink-0 items-center justify-center gap-2 text-neutral-white'
					onClick={goToPrevSpace}
					type='button'
				>
					<ArrowLeftIcon />
					{spaceNameToLabel[photosetSpaces[prevIndex].space.key_name]}
				</button>
				<div className='grid h-full w-full grid-rows-[auto_auto_minmax(0,1fr)] rounded-xl bg-neutral-white shadow-xl'>
					<div className='relative flex h-14 items-center justify-center font-bold text-neutral-900 text-sm [&_button]:absolute [&_button]:top-1/2 [&_button]:left-2 [&_button]:-translate-y-1/2'>
						<ButtonLegacy
							onClick={onClose}
							shape='square'
							type='button'
							variant='tertiary'
						>
							<XCloseIcon />
						</ButtonLegacy>
						<h3>
							{
								spaceNameToLabel[
									photosetSpaces[currentSpaceIndex].space
										.key_name
								]
							}
						</h3>
					</div>
					<ListHeader
						endSlot={
							<div className='flex items-center gap-4'>
								<SpaceDetailEndSlot
									allocateDropdown={
										<AllocateDropdown
											container={modalRef}
											excludedSpaces={[
												photosetSpaces[
													currentSpaceIndex
												].space.key_name
											]}
											onAllocate={onAllocate}
										/>
									}
									currentSpaceImageIds={currentSpaceImages.map(
										(image) => image.id
									)}
									currentSpaceImagesCount={
										currentSpaceImages.length
									}
									deallocateButton={
										<ButtonLegacy
											onClick={() =>
												onDeallocate({
													photosetSpaceId:
														photosetSpaces[
															currentSpaceIndex
														].id,
													imagesIds: selectedImages,
													keepSelected: false,
													trackInHistory: true
												})
											}
											size='sm'
											type='button'
											variant='secondary'
										>
											<MinusCircleIcon />
											{t('common:remove')}
										</ButtonLegacy>
									}
									firstImageId={currentSpaceImages[0]?.id}
									selectedImagesCount={selectedImages.length}
									selectImages={selectImages}
									setLastSelectedImageId={
										setLastSelectedImageId
									}
									unselectAll={unselectAll}
								/>
							</div>
						}
						size='sm'
						title={t('common:x_images', {
							count: currentSpaceImages.length
						})}
					/>
					<ScrollArea scrollbar={<Scrollbar className='mb-3' />}>
						<ImagesList>
							{currentSpaceImages.map((image, index, array) => {
								const isSelected = selectedImages.some(
									(selectedImageId) =>
										selectedImageId === image.id
								)
								return (
									<ImageCard
										image={image}
										isLoading={pendingAllocationImageIds.some(
											(pendingStatus) =>
												pendingStatus.imageIds.includes(
													image.id
												)
										)}
										isSelected={isSelected}
										key={image.id}
										onSelect={(isShiftPressed) => {
											handleImagesRangeSelect({
												array,
												image,
												index,
												isSelected,
												isShiftPressed,
												lastSelectedImageId,
												selectImages,
												setLastSelectedImageId,
												unselectImages
											})
										}}
										thumbnail={image.thumbnails?.xs}
									/>
								)
							})}
						</ImagesList>
					</ScrollArea>
				</div>
				<button
					className='flex w-[180px] shrink-0 items-center justify-center gap-2 text-neutral-white'
					onClick={goToNextSpace}
					type='button'
				>
					{spaceNameToLabel[photosetSpaces[nextIndex].space.key_name]}
					<ArrowRightIcon />
				</button>
			</div>
		</>,
		document.body
	)
}

SpaceDetailModal.displayName = 'SpaceDetailModal'
